import { instance } from '../api/api';

// Статусы финансовой записи — enum бэкенда, статус выставляет сервер
export type FinanceStatus = 'notPaid' | 'partiallyPaid' | 'paid';

// Деньги в ответах и запросах — десятичная строка с двумя знаками ("1234.50")
export interface IFinanceRecord {
  id: string;
  autoparkId: string;
  price: string;
  paid: string;
  remaining: string;
  status: FinanceStatus;
  dateOfRequest: string; // YYYY-MM-DD
  dateOfPaid: string | null; // YYYY-MM-DD, дата последней выплаты
  notes: string | null;
}

export interface IPagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface IFinanceResponse {
  data: IFinanceRecord[];
  pagination: IPagination;
}

export interface IFinanceFilters {
  page?: number;
  pageSize?: number;
  status?: FinanceStatus;
  autoparkID?: string;
  dateFrom?: string; // YYYY-MM-DD по Москве, включительно
  dateTo?: string;
}

export interface ICreateFinanceRequest {
  autoparkId: string;
  price: string;
  notes?: string;
  status?: 'notPaid';
}

export interface IUpdateFinanceRequest {
  notes?: string | null;
  price?: string; // только пока paid = 0
}

export interface IPayoutRequest {
  payoutUuid: string;
  amount: string;
  note?: string;
}

export interface IPayoutResponse {
  record: IFinanceRecord;
  payout: { uuid: string; amount: string; note: string | null; createdAt: string };
  autoparkBalance: string;
}

export type AutoparkOperationType = 'opening' | 'accrual' | 'reversal' | 'payout';

export interface IAutoparkOperation {
  uuid: string;
  type: AutoparkOperationType;
  amount: string; // со знаком: начисление +, сторно и выплата −
  createdAt: string; // RFC 3339
  roadlistId: string | null;
  roadlistNum: number | null;
  payoutUuid: string | null;
  note: string | null;
  balanceId: string | null;
}

export interface IOperationsFilters {
  page?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface IOperationsResponse {
  data: IAutoparkOperation[];
  pagination: IPagination;
  autoparkBalance: string;
}

// Ошибка API с HTTP-статусом и кодом из errors[0].code. status нет — ответа не было (сеть, таймаут).
export class FinanceApiError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'FinanceApiError';
    this.status = status;
    this.code = code;
  }
}

function toFinanceError(error: any, fallback: string): FinanceApiError {
  const status: number | undefined = error?.response?.status;
  const data = error?.response?.data;
  const first = Array.isArray(data?.errors) ? data.errors[0] : undefined;
  return new FinanceApiError(first?.message || data?.message || fallback, status, first?.code);
}

const EMPTY_PAGINATION: IPagination = { page: 1, pageSize: 10, totalItems: 0, totalPages: 1 };

// Сколько ждём ответа на выплату, прежде чем считать исход неизвестным
export const PAYOUT_TIMEOUT_MS = 20_000;

function toQuery(params: object): string {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });
  return queryParams.toString();
}

export const FinanceService = {
  async getBalances(filters: IFinanceFilters = {}): Promise<IFinanceResponse> {
    try {
      const response = await instance.get<IFinanceResponse>(`/balances?${toQuery(filters)}`);

      return response.data.data !== null ? response.data : { data: [], pagination: EMPTY_PAGINATION };
    } catch (error: any) {
      throw toFinanceError(error, 'Не удалось получить финансовые записи');
    }
  },

  async createBalance(data: ICreateFinanceRequest): Promise<IFinanceRecord> {
    try {
      const response = await instance.post<IFinanceRecord>('/balances', data);
      return response.data;
    } catch (error: any) {
      throw toFinanceError(error, 'Не удалось создать финансовую запись');
    }
  },

  async updateBalance(id: string, data: IUpdateFinanceRequest): Promise<IFinanceRecord> {
    try {
      const response = await instance.put<IFinanceRecord>(`/balances/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw toFinanceError(error, 'Не удалось обновить финансовую запись');
    }
  },

  async recordPayout(recordId: string, data: IPayoutRequest): Promise<IPayoutResponse> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PAYOUT_TIMEOUT_MS);
    try {
      // По таймауту обрываем запрос, чтобы поздний ответ не пересёкся с повтором
      const response = await instance.post<IPayoutResponse>(`/balances/${recordId}/payouts`, data, {
        signal: controller.signal,
      });
      return response.data;
    } catch (error: any) {
      throw toFinanceError(error, 'Не удалось записать выплату');
    } finally {
      clearTimeout(timer);
    }
  },

  async getOperations(autoparkId: string, filters: IOperationsFilters = {}): Promise<IOperationsResponse> {
    try {
      const response = await instance.get<IOperationsResponse>(
        `/autoparks/${autoparkId}/operations?${toQuery(filters)}`,
      );
      return { ...response.data, data: response.data.data ?? [] };
    } catch (error: any) {
      throw toFinanceError(error, 'Не удалось получить историю операций');
    }
  },
};
